import React from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Eyebrow } from "@/components/ui-bits";
import { COMPANY } from "@/data/site";
import { POLICIES, LEGAL_REVIEWED } from "@/data/legal";

const ABN = "23 943 384 114";
const ADDRESS = "Atkins Street, Kew VIC 3101";

const Paragraphs = ({ items, testId }) => (
  <>
    {items.map((text, i) => (
      <p key={i} className="text-sm sm:text-[15px] text-ei-graphite/90 leading-relaxed" data-testid={`${testId}-p-${i}`}>{text}</p>
    ))}
  </>
);

const PolicyTable = ({ table, testId }) => (
  <figure className="my-6 overflow-x-auto" data-testid={`${testId}-table`}>
    <figcaption className="mb-2 text-xs font-bold uppercase tracking-wide text-ei-golddark">{table.caption}</figcaption>
    <table className="w-full min-w-[460px] border-collapse text-sm">
      <thead>
        <tr className="bg-ei-navy text-ei-white text-left">
          {table.columns.map((column) => (
            <th key={column} className="px-4 py-2.5 font-semibold">{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, i) => (
          <tr key={row[0]} className={i % 2 ? "bg-white" : "bg-ei-white/60"}>
            {row.map((cell, j) => (
              <td key={j} className={`border-b border-ei-mist px-4 py-2.5 text-ei-graphite/90 ${j ? "tnum" : "font-medium text-ei-navy"}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </figure>
);

export default function Policy() {
  const { section } = useParams();
  const policy = POLICIES[section];

  if (!policy) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-ei-navy">Policy not found</h1>
        <Link to="/" className="mt-6 inline-block font-semibold text-ei-navy underline" data-testid="policy-not-found-home">Back to home</Link>
      </div>
    );
  }

  return (
    <>
      <Seo title={`${policy.title} — EI Battery Solutions`} />
      <section className="bg-ei-navy text-ei-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <Eyebrow className="text-ei-gold">Policy</Eyebrow>
          <h1 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">{policy.title}</h1>
          {policy.intro && <p className="mt-4 text-base md:text-lg text-ei-white/80 leading-relaxed">{policy.intro}</p>}
          <p className="mt-4 text-sm text-ei-white/60">
            EI Battery Solutions · ABN {ABN} · The Trustee for Yanakie Trust t/a EI Battery Solutions · {ADDRESS}
          </p>
          <p className="mt-2 text-sm text-ei-white/50" data-testid="policy-reviewed">Reviewed {LEGAL_REVIEWED}</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 lg:py-16" data-testid={`policy-${section}`}>
        <div className="space-y-10">
          {policy.sections.map((block, index) => {
            const testId = `policy-${section}-${index}`;
            return (
              <div key={block.heading} className="space-y-4" data-testid={testId}>
                <h2 className="text-base md:text-lg font-display font-bold text-ei-navy">{block.heading}</h2>
                <Paragraphs items={block.paragraphs} testId={testId} />
                {block.table && <PolicyTable table={block.table} testId={testId} />}
                {block.after && <Paragraphs items={block.after} testId={`${testId}-after`} />}
                {block.link && (
                  <Link to={block.link.to} className="inline-block text-sm font-semibold text-ei-navy underline underline-offset-4 hover:text-ei-golddark transition-colors" data-testid={`${testId}-link`}>
                    {block.link.label}
                  </Link>
                )}
                {block.links && (
                  <div className="flex flex-wrap gap-4">
                    {block.links.map((link) => (
                      <Link key={link.to} to={link.to} className="text-sm font-semibold text-ei-navy underline underline-offset-4 hover:text-ei-golddark transition-colors" data-testid={`${testId}-link-${link.to.split("/").pop()}`}>
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-12 text-sm text-ei-graphite/70">
          Questions about this policy? Contact us on{" "}
          <a href={COMPANY.phoneHref} className="text-ei-navy font-semibold underline">{COMPANY.phone}</a> or{" "}
          <a href={`mailto:${COMPANY.email}`} className="text-ei-navy font-semibold underline">{COMPANY.email}</a>.
        </p>
      </section>
    </>
  );
}
